'use client';

import { useEffect, useState } from 'react';
import { getServices } from '@/lib/actions/services';
import { stripHtml } from '@/lib/utils';
import { getServiceCategories } from '@/lib/actions/service-categories';
import { Link } from '@/i18n/routing';
import {
  Plus,
  Pencil,
  Target,
  ExternalLink,
  Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeleteServiceButton } from '@/components/admin/services/DeleteServiceButton';

export default function AdminServicesPage() {
  const [services, setServices] = useState<Awaited<ReturnType<typeof getServices>>>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});

  useEffect(() => {
    Promise.all([getServices(), getServiceCategories()]).then(([servicesData, categories]) => {
      setServices(servicesData);
      setCategoryMap(
        categories.reduce((acc: Record<string, string>, cat: any) => {
          acc[cat.id] = cat.name;
          return acc;
        }, {})
      );
    });
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy">Manage Services</h1>
          <p className="text-muted-foreground mt-1">Add and manage business services.</p>
        </div>
        <div className="flex gap-3">
          <Button asChild variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white">
            <Link href="/admin/services/categories">
              <Layers className="mr-2 h-4 w-4" /> Manage Categories
            </Link>
          </Button>
          <Button asChild className="bg-primary hover:bg-navy text-primary-foreground font-semibold">
            <Link href="/admin/services/new">
              <Plus className="mr-2 h-4 w-4" /> Add Service
            </Link>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Order</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Short Description</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <Target size={40} className="text-gray-300" />
                    <p>No services found. Add your first expertise!</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell className="font-medium">{service.order}</TableCell>
                  <TableCell>
                    <span className="font-semibold">{service.title}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs bg-light px-2 py-1 rounded-full font-bold text-navy">
                      {service.categoryId ? categoryMap[service.categoryId] || 'Unknown' : 'Uncategorized'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate text-sm text-gray-600">
                    {stripHtml(service.shortDescription)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" asChild title="View on Site">
                        <Link href={`/services/${service.slug}`} target="_blank">
                          <ExternalLink className="h-4 w-4 text-gray-500" />
                        </Link>
                      </Button>
                      <Button variant="ghost" size="icon" asChild title="Edit">
                        <Link href={`/admin/services/${service.id}`}>
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Link>
                      </Button>
                      <DeleteServiceButton id={service.id} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
